import axios from 'axios';
import { getCookie, clearCookie } from '../helper/lib';

const API_BASE_URL =
  process.env.REACT_APP_ENVIRONTMENT === 'staging'
    ? process.env.REACT_APP_API_URL_STAGING
    : process.env.REACT_APP_API_URL_PRODUCTION;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    api_key: process.env.REACT_APP_API_KEY,
    Accept: '*/*',
  },
  crossDomain: true,
  redirect: 'follow',
  referrerPolicy: 'no-referrer',
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = getCookie('token');

    if (token) {
      config.headers = {
        ...config.headers,
        token: token,
      };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        localStorage.clear();
        clearCookie('token');
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            registrations.forEach((registration) => {
              registration.unregister().then((success) => {
                if (success) {
                  console.log('Service Worker unregistered successfully.');
                }
              });
            });
          });
        }

        window.indexedDB.deleteDatabase('firebase-heartbeat-database');
        window.indexedDB.deleteDatabase('firebase-installations-database');
        window.indexedDB.deleteDatabase('firebase-messaging-database');
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// API Wrapper functions with try-catch
const apiWrapper = {
  get: async (url, params) => {
    try {
      const response = await api.get(url, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  patch: async (url, data) => {
    try {
      const response = await api.patch(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  post: async (url, data) => {
    try {
      const response = await api.post(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  put: async (url, data) => {
    try {
      const response = await api.put(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  delete: async (url, data) => {
    try {
      const response = await api.delete(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  multipartPost: async (url, formData, onUploadProgress) => {
    try {
      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            if (onUploadProgress) {
              onUploadProgress(percentCompleted);
            }
          }
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  multipartPatch: async (url, formData, onUploadProgress) => {
    try {
      const response = await api.patch(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            if (onUploadProgress) {
              onUploadProgress(percentCompleted);
            }
          }
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Create a separate axios instance without token interceptor
const apiNoToken = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    api_key: process.env.REACT_APP_API_KEY,
    Accept: '*/*',
  },
  crossDomain: true,
  redirect: 'follow',
  referrerPolicy: 'no-referrer',
});

// API Wrapper functions without token
const apiWrapperNoToken = {
  get: async (url, params) => {
    try {
      const response = await apiNoToken.get(url, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  post: async (url, data) => {
    try {
      const response = await apiNoToken.post(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  patch: async (url, data) => {
    try {
      const response = await apiNoToken.patch(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  put: async (url, data) => {
    try {
      const response = await apiNoToken.put(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  delete: async (url, data) => {
    try {
      const response = await apiNoToken.delete(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  multipartPost: async (url, formData, onUploadProgress) => {
    try {
      const response = await apiNoToken.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            if (onUploadProgress) {
              onUploadProgress(percentCompleted);
            }
          }
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export { apiWrapper, apiWrapperNoToken };
